import React, { useEffect, useState } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Chip, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, Stack, Tab,
  Tabs, Button, LinearProgress, Alert,
} from '@mui/material';
import { Payment, Warning, CheckCircle, Schedule, TrendingDown } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { loadPaymentDashboard } from '../../../reduxcomponents/slices/tripManagementSlice';
import PaymentScheduleModal from '../../query/PaymentScheduleModal';
import dayjs from 'dayjs';

const PAYMENT_STATUS_COLOR = {
  Paid: 'success', Partial: 'warning', Overdue: 'error', Pending: 'default',
};

const PaymentDashboard = () => {
  const dispatch = useDispatch();
  const { payments, loading } = useSelector(s => s.tripManagement);
  const [tab, setTab] = useState(0);
  const [collectDialog, setCollectDialog] = useState({ open: false, trip: null });

  const views = ['today', 'week', 'overdue', 'upcoming'];

  useEffect(() => {
    dispatch(loadPaymentDashboard(views[tab]));
  }, [tab, dispatch]);

  const totalDue = payments.reduce((sum, t) => sum + (t.payment?.due_amount || 0), 0);

  const handleCollect = (trip) => setCollectDialog({ open: true, trip });
  const handleCollectClose = () => {
    setCollectDialog({ open: false, trip: null });
    dispatch(loadPaymentDashboard(views[tab]));
  };

  return (
    <Box>
      {/* Summary Cards */}
      <Grid container spacing={2} mb={3}>
        {[
          { title: 'Total Due',       value: `₹${totalDue.toLocaleString()}`, icon: Payment,    color: 'error' },
          { title: 'Due Today',       value: payments.filter(t => views[tab] === 'today').length || '—',  icon: Schedule,   color: 'warning' },
          { title: 'Overdue',         value: payments.filter(t => t.payment?.status === 'Overdue').length, icon: Warning,   color: 'error' },
          { title: 'Paid This Month', value: '—', icon: CheckCircle, color: 'success' },
        ].map(({ title, value, icon: Icon, color }) => (
          <Grid item xs={12} sm={3} key={title}>
            <Card variant="outlined" sx={{ borderColor: `${color}.light`, borderRadius: 2 }}>
              <CardContent sx={{ p: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">{title}</Typography>
                    <Typography variant="h5" fontWeight={700} color={`${color}.main`}>{value}</Typography>
                  </Box>
                  <Icon color={color} sx={{ fontSize: 32 }} />
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* View Tabs */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
      >
        {['Due Today', 'Due This Week', 'Overdue', 'Upcoming'].map((label, i) => (
          <Tab key={i} label={label} />
        ))}
      </Tabs>

      {loading.payments && <LinearProgress sx={{ mb: 1 }} />}

      {payments.length === 0 && !loading.payments && (
        <Alert severity="success" icon={<CheckCircle />}>
          No pending payments for this period.
        </Alert>
      )}

      {payments.length > 0 && (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>Guest</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>Destination</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>Travel Date</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>Total Amt</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>Paid Amt</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>Due Amt</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>Due Date</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payments.map(trip => {
                const pct = trip.payment?.total_amount
                  ? Math.round((trip.payment.paid_amount / trip.payment.total_amount) * 100)
                  : 0;
                return (
                  <TableRow key={trip._id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{trip.guest_id?.name || '—'}</Typography>
                      <Typography variant="caption" color="text.secondary">{trip.guest_id?.phone}</Typography>
                    </TableCell>
                    <TableCell><Typography variant="body2">{trip.destination || '—'}</Typography></TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {trip.travel_date ? dayjs(trip.travel_date).format('DD MMM YY') : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        ₹{(trip.payment?.total_amount || 0).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" color="success.main">
                          ₹{(trip.payment?.paid_amount || 0).toLocaleString()}
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={pct}
                          color="success"
                          sx={{ height: 4, borderRadius: 2, mt: 0.5 }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="error.main" fontWeight={600}>
                        ₹{(trip.payment?.due_amount || 0).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color={trip.payment?.status === 'Overdue' ? 'error' : 'text.primary'}>
                        {trip.payment?.due_date ? dayjs(trip.payment.due_date).format('DD MMM YY') : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={trip.payment?.status || 'Pending'}
                        color={PAYMENT_STATUS_COLOR[trip.payment?.status] || 'default'}
                        size="small"
                        variant={trip.payment?.status === 'Overdue' ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        onClick={() => handleCollect(trip)}
                      >
                        Collect
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {collectDialog.trip && (
        <PaymentScheduleModal
          open={collectDialog.open}
          queryData={collectDialog.trip._raw}
          onClose={handleCollectClose}
        />
      )}
    </Box>
  );
};

export default PaymentDashboard;
